/**
 * Sales Items Section Component
 * Manages the list of items in a service sale
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Plus,
  Trash2,
  Edit2,
  Package,
  DollarSign,
  Calculator,
  Search,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils/formatters';
import { calculateSaleTotals } from '@/services/serviceSales';
import type { ServiceSaleFormItem, IndustryService } from '@/types/serviceSales';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';

const itemFormSchema = z.object({
  service_id: z.string().optional(),
  description: z.string().min(1, 'Descrição é obrigatória'),
  quantity: z.number().min(0.01, 'Quantidade deve ser maior que zero'),
  unit: z.string().min(1, 'Unidade é obrigatória'),
  unit_price: z.number().min(0, 'Valor unitário deve ser maior ou igual a zero'),
  discount_type: z.enum(['percentage', 'fixed']).default('percentage'),
  discount_value: z.number().min(0, 'Desconto deve ser maior ou igual a zero').default(0),
  tax_percentage: z.number().min(0, 'Imposto deve ser maior ou igual a zero').default(0),
  notes: z.string().optional(),
});

interface SalesItemsSectionProps {
  items: ServiceSaleFormItem[];
  services: IndustryService[];
  onUpdateItem: (index: number, updates: Partial<ServiceSaleFormItem>) => void;
  onRemoveItem: (index: number) => void;
  readOnly?: boolean;
}

interface EditItemDialogProps {
  item: ServiceSaleFormItem;
  services: IndustryService[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (item: ServiceSaleFormItem) => void;
}

function EditItemDialog({ item, services, open, onOpenChange, onSave }: EditItemDialogProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const form = useForm<z.infer<typeof itemFormSchema>>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: {
      service_id: item.service_id || '',
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      discount_type: item.discount_type || 'percentage',
      discount_value: item.discount_value || 0,
      tax_percentage: item.tax_percentage || 0,
      notes: item.notes || '',
    },
  });

  const watchedValues = form.watch();
  const selectedService = services.find(s => s.id === watchedValues.service_id);

  // Update form when service is selected
  React.useEffect(() => {
    if (selectedService && !watchedValues.description) {
      form.setValue('description', selectedService.name || '');
      form.setValue('unit_price', selectedService.price || 0);
      form.setValue('unit', selectedService.unit || 'un');
    }
  }, [selectedService, form]);

  // Calculate item total
  const itemTotal = React.useMemo(() => {
    const subtotal = watchedValues.quantity * watchedValues.unit_price;
    const discountAmount = watchedValues.discount_type === 'percentage'
      ? (subtotal * watchedValues.discount_value) / 100
      : watchedValues.discount_value;
    const taxable = subtotal - discountAmount;
    const taxAmount = (taxable * watchedValues.tax_percentage) / 100;
    return subtotal - discountAmount + taxAmount;
  }, [watchedValues]);

  const filteredServices = React.useMemo(() => {
    if (!debouncedSearch) return services;
    return services.filter(service =>
      service.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      service.description?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [services, debouncedSearch]);

  const handleSubmit = (values: z.infer<typeof itemFormSchema>) => {
    const updatedItem: ServiceSaleFormItem = {
      ...item,
      ...values,
      total_amount: itemTotal,
    };
    onSave(updatedItem);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar Item</DialogTitle>
          <DialogDescription>
            Configure os detalhes do item da venda
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="service_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Serviço (Opcional)</FormLabel>
                    <Select
                      value={field.value || ''}
                      onValueChange={(value) => {
                        field.onChange(value);
                        setSearchTerm('');
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Buscar serviço..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <div className="p-2 sticky top-0 bg-background border-b">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Buscar serviço..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="pl-8"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          <SelectItem value="custom">Item Personalizado</SelectItem>
                          {filteredServices.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              <div className="flex flex-col items-start">
                                <span className="font-medium">{service.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {formatCurrency(service.price)} / {service.unit}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </div>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unidade</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="un">Unidade</SelectItem>
                        <SelectItem value="hora">Hora</SelectItem>
                        <SelectItem value="dia">Dia</SelectItem>
                        <SelectItem value="mês">Mês</SelectItem>
                        <SelectItem value="projeto">Projeto</SelectItem>
                        <SelectItem value="m">Metro</SelectItem>
                        <SelectItem value="m²">Metro Quadrado</SelectItem>
                        <SelectItem value="m³">Metro Cúbico</SelectItem>
                        <SelectItem value="kg">Quilograma</SelectItem>
                        <SelectItem value="l">Litro</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Descrição detalhada do item..."
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantidade</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor Unitário (R$)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="discount_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Desconto</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="percentage">Percentual (%)</SelectItem>
                        <SelectItem value="fixed">Fixo (R$)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discount_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Desconto ({watchedValues.discount_type === 'percentage' ? '%' : 'R$'})
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tax_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Imposto (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações (Opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Observações sobre este item..."
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Item Total Preview */}
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total do Item:</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(itemTotal)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  <div>
                    Subtotal: {formatCurrency(watchedValues.quantity * watchedValues.unit_price)}
                  </div>
                  {watchedValues.discount_value > 0 && (
                    <div>
                      Desconto: -{formatCurrency(
                        watchedValues.discount_type === 'percentage'
                          ? (watchedValues.quantity * watchedValues.unit_price * watchedValues.discount_value) / 100
                          : watchedValues.discount_value
                      )}
                    </div>
                  )}
                  {watchedValues.tax_percentage > 0 && (
                    <div>
                      Imposto: +{formatCurrency(
                        ((watchedValues.quantity * watchedValues.unit_price -
                          (watchedValues.discount_type === 'percentage'
                            ? (watchedValues.quantity * watchedValues.unit_price * watchedValues.discount_value) / 100
                            : watchedValues.discount_value)) * watchedValues.tax_percentage) / 100
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar Item</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function SalesItemsSection({
  items,
  services,
  onUpdateItem,
  onRemoveItem,
  readOnly = false,
}: SalesItemsSectionProps) {
  const [editingItemIndex, setEditingItemIndex] = React.useState<number | null>(null);

  const totals = React.useMemo(() => {
    return calculateSaleTotals(items);
  }, [items]);

  const handleEditItem = (index: number) => {
    setEditingItemIndex(index);
  };

  const handleSaveItem = (item: ServiceSaleFormItem) => {
    if (editingItemIndex !== null) {
      onUpdateItem(editingItemIndex, item);
    }
    setEditingItemIndex(null);
  };

  const handleCloseEdit = () => {
    setEditingItemIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Items List */}
      <div className="space-y-3">
        {items.map((item, index) => (
          <Card key={item.id || index}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <h4 className="font-medium">{item.description}</h4>
                    {item.service_id && (
                      <Badge variant="secondary" className="text-xs">
                        Catálogo
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Quantidade:</span>
                      <div className="font-medium">
                        {item.quantity} {item.unit}
                      </div>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Valor Unitário:</span>
                      <div className="font-medium">
                        {formatCurrency(item.unit_price)}
                      </div>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Desconto:</span>
                      <div className="font-medium">
                        {item.discount_type === 'percentage'
                          ? `${item.discount_value}%`
                          : formatCurrency(item.discount_value)}
                      </div>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Total:</span>
                      <div className="font-medium text-primary">
                        {formatCurrency(item.total_amount)}
                      </div>
                    </div>
                  </div>

                  {item.notes && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Observações:</span>
                      <div className="mt-1">{item.notes}</div>
                    </div>
                  )}
                </div>

                {!readOnly && (
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditItem(index)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveItem(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Item Dialog */}
      {editingItemIndex !== null && (
        <EditItemDialog
          item={items[editingItemIndex]}
          services={services}
          open={editingItemIndex !== null}
          onOpenChange={handleCloseEdit}
          onSave={handleSaveItem}
        />
      )}

      {/* Summary */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between space-y-2">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Desconto:</span>
                <span className="text-red-600">-{formatCurrency(totals.discount_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Impostos:</span>
                <span className="text-green-600">+{formatCurrency(totals.tax_amount)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-medium text-lg">
                <span>Total:</span>
                <span className="text-primary">{formatCurrency(totals.total)}</span>
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {formatCurrency(totals.total)}
              </div>
              <div className="text-sm text-muted-foreground">
                {items.length} {items.length === 1 ? 'item' : 'itens'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}